import { Box, Stack, Typography, Divider } from "@mui/material";
import {
  Gavel,
  Person,
  Security,
  Copyright,
  Warning,
  Language,
  Link,
  Update,
  Policy,
} from "@mui/icons-material";
import useDocumentMeta from "../../utils/useDocumentMeta";

const TermsConditions = () => {
  useDocumentMeta(
    "Terms & Conditions | Radiance Aesthetics | Hindley | Wigan",
    "Terms and conditions for using the Radiance Aesthetics website and booking treatments at our Hindley, Wigan clinic.",
  );

  const termsItems = [
    {
      icon: <Gavel sx={{ color: "var(--color-text-muted)", fontSize: 20 }} />,
      title: "Introduction",
      content:
        "Welcome to the Radiance Aesthetics website. If you continue to browse and use our website, you are agreeing to comply with and be bound by the following terms and conditions, which together with our privacy policy govern the relationship between you and Radiance Aesthetics in relation to this website. Please understand that if you disagree with any part of these terms and conditions, you should not use this website. Note that in choosing to use our website, you are deemed to have read and agreed to the following terms and conditions.",
    },
    {
      icon: <Person sx={{ color: "#2196f3", fontSize: 20 }} />,
      title: "Terminology",
      content:
        '"Client", "You" and "Your" refers to you (the person accessing this website and accepting the Company\'s terms and conditions).\n\n"The Company", "Ourselves", "We" and "Us", refers to our Company (Radiance Aesthetics).\n\n"Party", "Parties", or "Us", refers to both the Client and ourselves, or either the Client or ourselves.\n\nAll terms refer to the use of this website.',
    },
    {
      icon: <Security sx={{ color: "#4caf50", fontSize: 20 }} />,
      title: "Privacy Statement",
      content: "Please see the Radiance Aesthetics Privacy Policy.",
    },
    {
      icon: <Copyright sx={{ color: "#ff9800", fontSize: 20 }} />,
      title: "Ownership & Copyright Notice",
      content:
        "The content found on the pages of this website is for your general information only. It is subject to change without notice and is the property of Radiance Aesthetics. This website contains material which is owned by or licensed to our clinic. This material may include, but is not limited to: the design, layout, look, appearance, graphics, videography, photography and website copy. Reproduction of any content found on this website is strictly prohibited, other than if written consent is provided by Radiance Aesthetics for you to do so.",
    },
    {
      icon: <Warning sx={{ color: "#f44336", fontSize: 20 }} />,
      title: "Exclusions and Limitations",
      content:
        "To the fullest extent permitted by law, Radiance Aesthetics excludes all representations and warranties relating to our website and its contents. We also exclude any liability for all content which is or may be provided by any affiliates another third party. Radiance Aesthetics excludes all liability for damages arising out of or in connection with your use of this website and our services.",
    },
    {
      icon: <Policy sx={{ color: "#9c27b0", fontSize: 20 }} />,
      title: "Limitation of Liability",
      content:
        "Radiance Aesthetics will not be liable for any damages, including, without limitation, those resulting from lost profits, arising from the Client using our website. Your use of any information, content or materials found on this website is entirely at your own risk, for which we shall never be liable. It is your own responsibility to ensure that any products, services or information available through this website meet your specific requirements.",
    },
    {
      icon: <Language sx={{ color: "#607d8b", fontSize: 20 }} />,
      title: "Availability",
      content:
        "Radiance Aesthetics will make all reasonable efforts to provide you with access to the website 24 hours per day, without interruptions. However, now and again you may experience disruptions due to essential maintenance or problems beyond our control. For this reason, we do not warrant that the website will be available continuously.",
    },
    {
      icon: <Security sx={{ color: "#795548", fontSize: 20 }} />,
      title: "Cookies",
      content:
        "This website uses cookies to monitor browsing preferences, and to enable us to retrieve user details for each visit, as well as to enable the functionality of the website and ease of use for those people visiting.",
    },
    {
      icon: <Link sx={{ color: "#3f51b5", fontSize: 20 }} />,
      title: "Links",
      content:
        "You do not have permission to create a link to any page of this website without our prior written consent. These links are provided for your convenience to provide further information, and do not signify that we endorse the website(s) or any content found therein.",
    },
    {
      icon: <Gavel sx={{ color: "#e91e63", fontSize: 20 }} />,
      title: "General & Waiver",
      content:
        "The laws of England, Scotland, Northern Ireland and Wales govern these Terms and Conditions. By using this website, you consent to these terms and conditions without disputes. Unauthorised use of this website may give rise to a claim for damages and/or be a criminal offence.",
    },
    {
      icon: <Update sx={{ color: "#009688", fontSize: 20 }} />,
      title: "Notification of Changes",
      content:
        "Radiance Aesthetics reserves the right to change these conditions from time to time. You are therefore advised to re-read this statement on a regular basis.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: "var(--color-bg-warm)",
        padding: { xs: "40px 16px", md: "64px 24px" },
        minHeight: "60vh",
      }}
    >
      <Stack
        sx={{
          maxWidth: "720px",
          margin: "0 auto",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: "var(--color-accent)",
            textAlign: "center",
            mb: 1.5,
          }}
        >
          Legal
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-primary)",
            fontWeight: 400,
            textAlign: "center",
            fontSize: { xs: "28px", md: "40px" },
            mb: 1.5,
          }}
          variant="h1"
        >
          Terms & Conditions
        </Typography>

        <Typography
          sx={{
            color: "var(--color-text-light)",
            textAlign: "center",
            fontSize: "16px",
            mb: 5,
            maxWidth: "600px",
            lineHeight: 1.7,
            fontFamily: "var(--font-body)",
          }}
        >
          Please review our terms and conditions for using this website.
        </Typography>

        <Stack
          gap={0}
          sx={{
            width: "100%",
            backgroundColor: "var(--color-white)",
            borderRadius: "8px",
            border: "1px solid var(--color-border)",
            overflow: "hidden",
          }}
        >
          {termsItems.map((item, index) => (
            <Box key={index}>
              <Stack
                direction="row"
                gap={2}
                sx={{
                  alignItems: "flex-start",
                  p: { xs: 2.5, md: 3 },
                }}
              >
                <Box sx={{ mt: 0.25, flexShrink: 0 }}>{item.icon}</Box>
                <Stack gap={0.75} sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "var(--color-primary)",
                      fontFamily: "var(--font-body)",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: 1.7,
                      color: "var(--color-text-light)",
                      fontFamily: "var(--font-body)",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {item.content}
                  </Typography>
                </Stack>
              </Stack>
              {index < termsItems.length - 1 && (
                <Divider sx={{ opacity: 0.5 }} />
              )}
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 4,
            p: 2,
            backgroundColor: "var(--color-white)",
            borderRadius: "8px",
            width: "100%",
            border: "1px solid var(--color-border)",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "var(--color-text-muted)",
              fontStyle: "italic",
              textAlign: "center",
              fontFamily: "var(--font-body)",
            }}
          >
            By using this website, you acknowledge that you have read,
            understood, and agree to be bound by these terms and conditions.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default TermsConditions;
